import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FireStage } from "@/components/camp/FireStage";
import { RoomModeStage } from "@/components/camp/RoomModeStage";
import { PANEL_MODE } from "@/components/room/roomModePanel";
import type { RoomMode } from "@/engine/state/roomControl";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

import {
  fireCast,
  fireStageMode,
  gridCast,
  type You,
} from "./createPreviewCast";

/** How long one beat of the preview lasts. */
const BEAT_MS = 2200;
/** How long a pressed burst of reactions stays up. */
const BURST_MS = 1200;

/** The beat the preview is on, moving on by itself unless motion is reduced. */
function useBeat(still: boolean) {
  const [beat, setBeat] = useState(0);
  const [burst, setBurst] = useState(false);
  useEffect(() => {
    if (still) return;
    const timer = setInterval(() => setBeat((b) => b + 1), BEAT_MS);
    return () => clearInterval(timer);
  }, [still]);
  useEffect(() => {
    if (!burst) return;
    const timer = setTimeout(() => setBurst(false), BURST_MS);
    return () => clearTimeout(timer);
  }, [burst]);
  const next = () => {
    setBeat((b) => b + 1);
    setBurst(true);
  };
  return { beat, burst, next };
}

/**
 * The kind of room being chosen, played rather than pictured (#779): a cast of
 * six meeting as they would in it — who talks moves round, reactions float up,
 * a hand goes up, somebody walks over or crosses to a team. Pressing it plays
 * the next moment at once, with a burst of reactions.
 */
export function CreatePreview({ mode, you }: { mode: RoomMode; you: You }) {
  const { t } = useTranslation();
  const still = usePrefersReducedMotion();
  const { beat, burst, next } = useBeat(still);
  const fire = fireStageMode(mode);

  return (
    <button
      type="button"
      data-testid="create-preview"
      data-beat={beat}
      aria-label={t("entry.previewNext")}
      onClick={next}
      className="block h-full w-full cursor-pointer overflow-hidden rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_.group]:transition-[left,top] [&_.group]:duration-(--motion-slow) [&_.group]:ease-(--motion-enter)"
    >
      {fire ? (
        <FireStage
          people={fireCast(you, beat, fire, burst)}
          mode={fire}
          // Six in a preview the size of a card: smaller figures on a wider
          // ring, so nobody stands on anybody.
          className="h-full min-h-0 [--fig-h:64px] [--ring-rx:36%] sm:[--fig-h:72px] sm:[--ring-rx:38%] lg:[--fig-h:80px]"
        />
      ) : (
        <RoomModeStage
          mode={PANEL_MODE[mode]}
          // The plain room's tiles are the demo's size, two to a row: four is
          // what the frame holds (#805).
          people={
            mode === "plain"
              ? gridCast(you, beat).slice(0, 4)
              : gridCast(you, beat)
          }
          teamOneLabel={t("roomAreas.team1")}
          teamTwoLabel={t("roomAreas.team2")}
          hallLabel={t("roomAreas.hall")}
          className="h-full min-h-0"
        />
      )}
    </button>
  );
}
